import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileCheck2, Building2, UserPlus, CheckCircle2, AlertTriangle, Clock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { api, formatDateBR, formatDateTimeBR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const ProvidenciaBadge = ({ status }) => {
  const map = {
    em_dia: { label: "Em Dia", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    concluida: { label: "Em Dia", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    aguardando: { label: "Em Providência", cls: "bg-amber-50 text-amber-900 border-amber-200" },
    em_atraso: { label: "Em Atraso", cls: "bg-red-50 text-red-800 border-red-200" },
  };
  if (!status) return <span className="text-xs text-slate-400">— sem providência —</span>;
  const s = map[status] || map.em_dia;
  return <Badge variant="outline" className={`${s.cls} border text-xs`}>{s.label}</Badge>;
};

const KpiCard = ({ icon: Icon, label, value, hint, accent, to, testId }) => {
  const navigate = useNavigate();
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => to && navigate(to)}
      onKeyDown={(e) => { if (to && (e.key === "Enter" || e.key === " ")) navigate(to); }}
      className="card-soft cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-midnight"
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
            {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
          </div>
          <div className={`p-2 rounded-md ${accent || "bg-slate-100 text-slate-700"}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function SecretariaDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const { kpis, extras, contracts } = data;

  return (
    <div className="space-y-6" data-testid="secretaria-dashboard">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Painel da Secretária</h1>
        <p className="text-sm text-slate-600 mt-1">
          Visão operacional dos contratos. Clique nos cards para ver as listas filtradas.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          icon={FileCheck2}
          label="Contratos Ativos"
          value={kpis.active_contracts}
          hint="Em execução"
          accent="bg-emerald-50 text-emerald-700"
          to="/secretaria/contratos"
          testId="kpi-contratos-ativos"
        />
        <KpiCard
          icon={UserPlus}
          label="Novo Cliente"
          value={extras?.new_clients_pending ?? 0}
          hint="Aguardando andamento"
          accent="bg-blue-50 text-blue-700"
          to="/secretaria/novos-clientes"
          testId="kpi-novo-cliente"
        />
        <KpiCard
          icon={CheckCircle2}
          label="Em Dia"
          value={extras?.providencia_em_dia ?? 0}
          hint="Providências em dia"
          accent="bg-emerald-50 text-emerald-700"
          to="/secretaria/contratos?providencia=em_dia"
          testId="kpi-em-dia"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Em Atraso"
          value={extras?.providencia_em_atraso ?? 0}
          hint="Providências atrasadas"
          accent="bg-red-50 text-red-700"
          to="/secretaria/contratos?providencia=em_atraso"
          testId="kpi-em-atraso"
        />
        <KpiCard
          icon={Clock}
          label="Em Providência"
          value={extras?.providencia_em_providencia ?? 0}
          hint="Aguardando andamento"
          accent="bg-amber-50 text-amber-700"
          to="/secretaria/contratos?providencia=aguardando"
          testId="kpi-em-providencia"
        />
      </div>

      <Card className="card-soft">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Contratos sob gestão</CardTitle>
          <Link to="/secretaria/contratos" className="text-xs font-medium text-midnight hover:underline">
            Ver todos
          </Link>
        </CardHeader>
        <CardContent>
          {contracts.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              Nenhum contrato cadastrado.
            </div>
          ) : (
            <div className="space-y-3">
              {contracts.map((c) => (
                <Link
                  key={c.id}
                  to={`/secretaria/contratos/${c.id}`}
                  className="block p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`contract-card-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="font-medium text-slate-900">{c.client_name}</span>
                        <span className="text-slate-400">·</span>
                        <span className="text-sm text-slate-600">{c.product_name}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <ProvidenciaBadge status={c.providencia_status} />
                        {c.consultant_name && (
                          <span className="text-xs text-slate-500">
                            Consultor: {c.consultant_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <div>Prazo: {formatDateBR(c.end_date)}</div>
                      <div className={c.days_remaining < 30 ? "text-red-600 font-medium" : ""}>
                        {c.days_remaining < 0 ? "Vencido" : `${c.days_remaining} dias`}
                      </div>
                      {c.next_meeting && (
                        <div className="mt-1">
                          Próxima: {formatDateTimeBR(c.next_meeting.scheduled_at)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <Progress value={c.progress_pct} className="h-1.5 flex-1" />
                    <span className="text-xs text-slate-600 w-12 text-right">{c.progress_pct}%</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
